import { useState, type FormEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, CheckCircle } from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { PageHeader } from '@/components/PageHeader'
import { DataTable } from '@/components/DataTable'
import { AsyncBoundary } from '@/components/AsyncBoundary'

interface RoyaltyRow {
  id: string
  source: string
  periodStart: string
  periodEnd: string
  amountPence: number
  currency: string
  notes?: string
  reconciled: boolean
  createdAt: string
}

export function RoyaltiesPage() {
  const qc = useQueryClient()
  const [adding, setAdding] = useState(false)
  const { data, error, isLoading } = useQuery<RoyaltyRow[], ApiClientError>({
    queryKey: ['royalties'],
    queryFn: () => artistFetch<RoyaltyRow[]>('/royalties'),
  })

  async function reconcile(id: string) {
    await artistFetch(`/royalties/${id}/reconcile`, { method: 'POST' })
    qc.invalidateQueries({ queryKey: ['royalties'] })
  }

  return (
    <div>
      <PageHeader
        title="Royalties & Sync"
        subtitle="Royalty statements, sync monitoring, and reconciliation tracking."
        actions={
          <button
            type="button"
            onClick={() => setAdding((a) => !a)}
            className="flex items-center gap-2 rounded-md bg-ovation-ink px-3 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90"
          >
            <Plus className="h-4 w-4" />
            {adding ? 'Cancel' : 'Add statement'}
          </button>
        }
      />
      {adding && (
        <AddRoyaltyForm
          onCreated={() => {
            setAdding(false)
            qc.invalidateQueries({ queryKey: ['royalties'] })
          }}
        />
      )}
      <AsyncBoundary isLoading={isLoading} error={error}>
        <DataTable
          rows={data ?? []}
          rowKey={(r) => r.id}
          emptyMessage="No royalty statements recorded."
          columns={[
            {
              key: 'source',
              header: 'Source',
              render: (r) => <span className="uppercase">{r.source}</span>,
            },
            {
              key: 'period',
              header: 'Period',
              render: (r) =>
                `${new Date(r.periodStart).toLocaleDateString()} - ${new Date(r.periodEnd).toLocaleDateString()}`,
            },
            {
              key: 'amount',
              header: 'Amount',
              align: 'right',
              render: (r) => {
                const symbol = r.currency === 'GBP' ? '£' : r.currency === 'USD' ? '$' : r.currency
                return `${symbol}${(r.amountPence / 100).toFixed(2)}`
              },
            },
            { key: 'currency', header: 'Currency', render: (r) => r.currency },
            {
              key: 'reconciled',
              header: 'Status',
              render: (r) =>
                r.reconciled ? (
                  <span className="flex items-center gap-1 text-green-700">
                    <CheckCircle className="h-3 w-3" /> Reconciled
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => reconcile(r.id)}
                    className="text-sm text-blue-700 hover:underline"
                  >
                    Reconcile
                  </button>
                ),
            },
            {
              key: 'notes',
              header: 'Notes',
              render: (r) => r.notes ?? '—',
            },
          ]}
        />
      </AsyncBoundary>
    </div>
  )
}

function AddRoyaltyForm({ onCreated }: { onCreated: () => void }) {
  const [source, setSource] = useState('streaming')
  const [periodStart, setPeriodStart] = useState('')
  const [periodEnd, setPeriodEnd] = useState('')
  const [amount, setAmount] = useState('')
  const [currency, setCurrency] = useState('GBP')
  const [notes, setNotes] = useState('')
  const [error, setErr] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setErr(null)
    setSubmitting(true)
    try {
      await artistFetch('/royalties', {
        method: 'POST',
        body: {
          source,
          periodStart: new Date(periodStart).toISOString(),
          periodEnd: new Date(periodEnd).toISOString(),
          amountPence: Math.round(Number(amount) * 100),
          currency,
          notes: notes || undefined,
        },
      })
      onCreated()
    } catch (err) {
      setErr(err instanceof ApiClientError ? err.message : 'Error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={onSubmit} className="mb-6 space-y-4 rounded-lg border border-ovation-ink/10 bg-white p-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Source</label>
          <select value={source} onChange={(e) => setSource(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm">
            <option value="ascap">ASCAP</option>
            <option value="bmi">BMI</option>
            <option value="prs">PRS</option>
            <option value="socan">SOCAN</option>
            <option value="sync">Sync</option>
            <option value="streaming">Streaming</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Period start</label>
          <input type="date" required value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Period end</label>
          <input type="date" required value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Amount (major units)</label>
          <input type="number" step="0.01" required value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" placeholder="1234.56" />
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Currency</label>
          <select value={currency} onChange={(e) => setCurrency(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm">
            <option value="GBP">GBP</option>
            <option value="USD">USD</option>
            <option value="EUR">EUR</option>
            <option value="CAD">CAD</option>
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs uppercase tracking-wider text-ovation-muted">Notes</label>
          <input value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full rounded-md border border-ovation-ink/15 px-3 py-2 text-sm" />
        </div>
      </div>
      {error && <div className="text-sm text-red-700">{error}</div>}
      <button type="submit" disabled={submitting} className="rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50">
        {submitting ? 'Creating...' : 'Create statement'}
      </button>
    </form>
  )
}
